import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { Box, Grid, IconButton, Typography } from '@mui/material';
import { find, first, groupBy } from 'lodash';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ActionBar, Header } from 'src/components/header';
import { Modal, PreviewModal } from 'src/components/modal';
import { TemplateVariations } from 'src/components/productVariationTable/TemplateVariations';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import { PATHS } from 'src/constants/paths';
import {
  useGetCCAssets,
  useGetProduct,
  useGetProductCategories,
  useGetProductVariationFlavors,
  useGetProductVariationTypes,
  useModal,
} from 'src/hooks';
import { getCompareAndFilteredData, lang } from 'src/utils';
import { getAssetFromAssetId } from 'src/utils/gcf-helper';

import {
  NoLeftSpacedItem,
  ProductNameText,
  ProductSizeText,
  ScrollableContainer,
  StyledAccordion,
  StyledAccordionDetails,
  StyledAccordionItemWrapper,
  StyledChipContainer,
  StyledDialogContent,
  StyledDialogTitle,
  StyledDivider,
  StyledDynamicTooltipChip,
  StyledGrid,
  StyledLabelValue,
  StyledProductImage,
} from './ReviewProductPage.styled';

const ProductName = ({
  name,
  width,
  variant,
  component,
  marginBottom = 0,
}: {
  name: string | null | undefined;
  width?: number;
  variant: string;
  component?: string;
  marginBottom?: number;
}) => {
  return (
    <CustomTooltip
      width={500}
      placement="bottom"
      title={name}
      overflowLineNumber={1}>
      <ProductNameText
        variant={variant}
        component={component}
        mb={marginBottom}
        $width={width}>
        {name}
      </ProductNameText>
    </CustomTooltip>
  );
};

export const ReviewProductPage = () => {
  const productInfoModal = useModal();

  //Make all api calls for fetching data
  const { productCode = '' } = useParams();
  const productData = useGetProduct(productCode.toString());
  const { data: productCategories = [] } = useGetProductCategories([
    'sort=*name',
  ]);
  const { data: productVariationTypes = [] } = useGetProductVariationTypes([
    'sort=*name',
  ]);
  const { data: productVariationFlavors = [] } = useGetProductVariationFlavors([
    'sort=*name',
  ]);
  const { data: assets = [] } = useGetCCAssets();
  const [modelOpen, setModelOpen] = React.useState(false);
  const navigate = useNavigate();

  // Cleanup all the api data before render
  const assetRecord = getAssetFromAssetId(
    assets,
    first(productData.data?.asset_id),
  );

  const selectedChipCategory = productCategories.filter((category) => {
    const categoryId = category.id;

    return productData.data?.product_category_id?.includes(categoryId);
  });

  const groupByVariationSizeId = groupBy(
    productData.data?.productVariations,
    'product_variation_size_id',
  );

  return (
    <Grid>
      <Header
        actionBar={
          <ActionBar
            type={lang.reviewProducts}
            onHandleClick={() => navigate(PATHS.PRODUCT_EDIT(productCode))}
            onDeleteClick={() => {}}
          />
        }
        breadcrumbItems={[
          {
            label: lang.products,
            link: PATHS.PRODUCTS,
          },
          {
            label: lang.details,
          },
        ]}
        title={`${productData?.data?.productVariations[0]?.name}`}
      />

      <StyledGrid container spacing={3}>
        <NoLeftSpacedItem item onClick={productInfoModal.onOpenModal}>
          <StyledProductImage
            sx={{ cursor: 'pointer', margin: 'auto' }}
            src={assetRecord?.thumbnailUrl}
            alt="preview asset"
          />
        </NoLeftSpacedItem>
        <Grid container rowSpacing={2} item xs={7}>
          <Grid item xs={6}>
            <Box>
              <Typography variant="h6" component="h3">
                Group status
              </Typography>
              <Typography variant="body1">
                {productData.data?.active ? lang.activeText : lang.inactiveText}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box>
              <Typography variant="h6" component="h3">
                Group product code
              </Typography>
              <Typography variant="body1">
                {productData.data?.product_code}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box>
              <Typography variant="h6" component="h3">
                Product name override
              </Typography>
              <ProductName
                variant="body1"
                width={70}
                name={productData.data?.name_override}
              />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6" component="h3">
              Product category tags
            </Typography>
            <Box>
              {selectedChipCategory.map((category) => (
                <StyledChipContainer>
                  <StyledDynamicTooltipChip
                    key={category.id}
                    chipName={category?.name}
                    tooltipTitle={category?.name}
                    tooltipWidth={500}
                    tooltipActivationWidth={252}
                  />
                </StyledChipContainer>
              ))}
            </Box>
          </Grid>
        </Grid>
      </StyledGrid>
      <Modal
        open={modelOpen}
        onClose={() => setModelOpen(false)}
        fullWidth
        maxWidth="xl">
        <IconButton
          aria-label="close"
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}>
          <CloseIcon onClick={() => setModelOpen(false)} />
        </IconButton>

        <CustomTooltip
          width={500}
          placement="bottom"
          title={productData?.data?.productVariations[0]?.name}
          overflowLineNumber={1}>
          <StyledDialogTitle variant="h2" aria-label="preview-dialog-title">
            {productData?.data?.productVariations[0]?.name}
          </StyledDialogTitle>
        </CustomTooltip>

        <StyledDialogContent
          sx={{
            background: '#F5F5F5',
            border: '1px solid #898C90',
            margin: '15px',
          }}>
          <StyledProductImage
            sx={{
              width: '500px',
              height: '450px',
              margin: ' auto',
              background: '#FFFFFF',
              border: 'none',
            }}
            src={assetRecord?.thumbnailUrl}
            alt="preview asset"
          />
        </StyledDialogContent>
      </Modal>
      <StyledDivider />

      <ScrollableContainer>
        {Object.keys(groupByVariationSizeId).map((sizeId, key) => (
          <Box key={key}>
            <Box>
              <ProductSizeText component="h2" mb={1}>
                {find(productVariationTypes, { id: parseInt(sizeId) })?.name ||
                  'None'}
              </ProductSizeText>
            </Box>
            {groupByVariationSizeId[sizeId].map((item, key) => (
              <StyledAccordion key={key}>
                <StyledAccordionItemWrapper
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1a-content"
                  id="header">
                  <ProductName
                    variant="h4"
                    component="h2"
                    marginBottom={1}
                    name={item.name}
                  />
                </StyledAccordionItemWrapper>
                <StyledAccordionDetails key={key}>
                  <Grid container rowSpacing={3}>
                    <Grid item xs={6}>
                      <Typography variant="h6" component="h3">
                        Product code
                      </Typography>
                      <Typography variant="body1">
                        {item.product_code}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="h6" component="h3">
                        Product variation type
                      </Typography>
                      <StyledLabelValue>
                        {
                          getCompareAndFilteredData(
                            [item],
                            productVariationTypes,
                            'product_variation_size_id',
                          )?.name
                        }
                      </StyledLabelValue>
                    </Grid>
                    <Grid item xs={6} paddingRight={3}>
                      <Typography variant="h6" component="h3">
                        Product name
                      </Typography>
                      <ProductName variant="body1" name={item.name} />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="h6" component="h3">
                        Status
                      </Typography>
                      <Typography variant="body1">
                        {item.active ? lang.activeText : lang.inactiveText}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="h6" component="h3">
                        Description
                      </Typography>
                      <Typography variant="body1">
                        {item.description}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="h6" component="h3">
                        Disclaimer
                      </Typography>
                      <Typography variant="body1">{item.disclaimer}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="h6" component="h3">
                        Volume
                      </Typography>
                      <Typography variant="body1">{item.volume}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="h6" component="h3">
                        Flavor
                      </Typography>
                      <ProductName
                        name={
                          getCompareAndFilteredData(
                            [item],
                            productVariationFlavors,
                            'product_variation_flavor_id',
                          )?.name
                        }
                        variant="body1"
                      />
                    </Grid>
                    <Grid item xs={4} pl={1}>
                      <Typography variant="h6" component="h3">
                        Nutrition
                      </Typography>
                      <Typography variant="body1">{item.nutrition}</Typography>
                    </Grid>
                    {item.variation_template_id?.length > 0 && (
                      <Grid item container xs={12}>
                        <Grid item xs={12}>
                          <Typography variant="h6" component="h3">
                            {lang.dyDisplay}
                          </Typography>
                        </Grid>

                        <Grid item xs={3}>
                          <Typography>
                            {item.variation_template?.template_name?.replace(
                              '_',
                              ' ',
                            )}
                          </Typography>
                        </Grid>

                        <TemplateVariations
                          nameOverride={productData.data?.name_override || ''}
                          productVariation={item}
                          productVariationTypes={productVariationTypes}
                          productVariationFlavors={productVariationFlavors}
                          renderItem={(value) => (
                            <CustomTooltip
                              title={value}
                              overflowLineNumber={1}
                              placement="bottom">
                              <Typography>{value}</Typography>
                            </CustomTooltip>
                          )}
                        />
                      </Grid>
                    )}
                  </Grid>
                </StyledAccordionDetails>
              </StyledAccordion>
            ))}
          </Box>
        ))}
      </ScrollableContainer>
      {productInfoModal.open && assetRecord && (
        <PreviewModal
          open={productInfoModal.open}
          onClose={productInfoModal.onCloseModal}
          type="product"
          content={{
            ...assetRecord,
            name: assetRecord?.name || '',
          }}
          hideButtons={true}
        />
      )}
    </Grid>
  );
};
